"use client";

import type {
  Cell,
  Column,
  Header,
  HeaderGroup,
  Row,
  Table,
} from "@tanstack/react-table";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnPinningState,
  type PaginationState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

// Re-export ColumnDef for use in other components
export type { ColumnDef };
import { ChevronDown, Columns, ChevronRight, Filter } from "lucide-react";
import * as React from "react";
import { DropdownMenu } from "../input/dropdown-menu";
import { Pagination } from "../navigation/pagination";
import { Text } from "./text";
import { Input } from "../input/input";
import { Checkbox } from "../input/checkbox";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../container/popover";
import { cn } from "@/utils/utils";

/**
 * Generic, shadcn-style DataTable block, powered by TanStack Table.
 * - Filtering (single text filter)
 * - Sorting
 * - Column Visibility
 * - Pagination
 * - Row Selection
 * - Expandable Rows
 */
export type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  className?: string;

  // Toolbar
  filterColumn?: string; // accessorKey to filter against
  filterPlaceholder?: string;

  // Layout toggles
  showToolbar?: boolean;
  showFooter?: boolean;

  emptyState?: React.ReactNode;
  showBorder?: boolean;
  enablePageSize?: boolean;
  filters?: React.ReactNode;
  toolbarActions?: React.ReactNode; // Slot for bulk actions or other toolbar actions
  toolbarRightActions?: React.ReactNode; // Slot for actions on the right side of toolbar
  // Controlled row selection
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  // Expandable rows
  renderExpandedContent?: (row: Row<TData>) => React.ReactNode;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  expandedRows?: Record<string, boolean>;
  onExpandedRowsChange?: (expanded: Record<string, boolean>) => void;
  // Row ID function
  getRowId?: (row: TData, index: number) => string;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  filterColumn,
  filterPlaceholder = "Filter...",
  showToolbar = true,
  showFooter = true,
  emptyState,
  showBorder = true,
  enablePageSize = false,
  filters,
  toolbarActions,
  toolbarRightActions,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  renderExpandedContent,
  getRowCanExpand,
  expandedRows: controlledExpandedRows,
  onExpandedRowsChange,
  getRowId,
  pinnedLeftColumns,
  pinnedRightColumns,
}: DataTableProps<TData, TValue> & { pinnedLeftColumns?: string[], pinnedRightColumns?: string[] }) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>({
    left: pinnedLeftColumns || [],
    right: pinnedRightColumns || [],
  });
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [internalRowSelection, setInternalRowSelection] = React.useState({});
  const [internalExpandedRows, setInternalExpandedRows] = React.useState<
    Record<string, boolean>
  >({});
  const [lockedColumnWidths, setLockedColumnWidths] = React.useState<
    Record<string, number>
  >({});
  const tableRef = React.useRef<HTMLTableElement>(null);
  const hasMeasuredWidths = React.useRef(false);

  // Use controlled or internal state
  const rowSelection =
    controlledRowSelection !== undefined
      ? controlledRowSelection
      : internalRowSelection;
  const setRowSelection = React.useCallback(
    (updater: any) => {
      const currentSelection =
        controlledRowSelection !== undefined
          ? controlledRowSelection
          : internalRowSelection;
      const newSelection =
        typeof updater === "function" ? updater(currentSelection) : updater;
      if (onRowSelectionChange) {
        onRowSelectionChange(newSelection);
      } else {
        setInternalRowSelection(newSelection);
      }
    },
    [controlledRowSelection, internalRowSelection, onRowSelectionChange]
  );

  // Expanded rows state
  const expandedRows =
    controlledExpandedRows !== undefined
      ? controlledExpandedRows
      : internalExpandedRows;
  const setExpandedRows = React.useCallback(
    (updater: any) => {
      const currentExpanded =
        controlledExpandedRows !== undefined
          ? controlledExpandedRows
          : internalExpandedRows;
      const newExpanded =
        typeof updater === "function" ? updater(currentExpanded) : updater;
      if (onExpandedRowsChange) {
        onExpandedRowsChange(newExpanded);
      } else {
        setInternalExpandedRows(newExpanded);
      }
    },
    [controlledExpandedRows, internalExpandedRows, onExpandedRowsChange]
  );

  // Ensure columns and data are valid
  const validColumns = React.useMemo(() => columns || [], [columns]);
  const validData = React.useMemo(() => data || [], [data]);

  // Custom filter function for array-based multi-select filters
  const arrayIncludesFilter = React.useCallback(
    (row: Row<TData>, columnId: string, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) return true;
      const cellValue = String(row.getValue(columnId) ?? "");
      return filterValue.includes(cellValue);
    },
    []
  );

  // Text search filter function - searches case-insensitively across all rows
  const includesStringFilter = React.useCallback(
    (row: Row<TData>, columnId: string, filterValue: string | string[]) => {
      // Handle array values (convert to string)
      const filterStr = Array.isArray(filterValue)
        ? filterValue.join(" ")
        : String(filterValue ?? "");

      if (!filterStr || filterStr.trim() === "") return true;
      const cellValue = String(row.getValue(columnId) ?? "").toLowerCase();
      const searchValue = filterStr.toLowerCase().trim();
      return cellValue.includes(searchValue);
    },
    []
  );

  const table = useReactTable({
    data: validData,
    columns: validColumns.map((col) => {
      // Set custom filter function for columns that support filtering
      if (
        "accessorKey" in col &&
        col.accessorKey &&
        col.enableColumnFilter !== false
      ) {
        // Use text search filter for the filterColumn, array filter for others
        const isFilterColumn = filterColumn && col.accessorKey === filterColumn;
        return {
          ...col,
          filterFn: isFilterColumn ? includesStringFilter : arrayIncludesFilter,
        };
      }
      return col;
    }),
    getRowId: getRowId,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnPinning,
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    enableColumnFilters: true,
    enableColumnPinning: true,
    filterFromLeafRows: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filterCol = filterColumn ? table.getColumn(filterColumn) : undefined;

  // Measure and lock column widths on initial mount (before any filters)
  React.useEffect(() => {
    if (hasMeasuredWidths.current || tableRef.current === null) return;
    if (validData.length === 0) return; // Wait for data to be loaded

    // Use a timeout to ensure table is fully rendered
    const timeoutId = setTimeout(() => {
      if (!tableRef.current || hasMeasuredWidths.current) return;

      // Measure column widths from the table headers using data attributes
      const headerGroups = table.getHeaderGroups();
      const widths: Record<string, number> = {};

      headerGroups.forEach((headerGroup) => {
        headerGroup.headers.forEach((header) => {
          const th = tableRef.current?.querySelector(
            `th[data-column-id="${header.column.id}"]`
          ) as HTMLElement | null;
          if (th && header.column && th.offsetWidth > 0) {
            widths[header.column.id] = th.offsetWidth;
          }
        });
      });

      if (Object.keys(widths).length > 0) {
        setLockedColumnWidths(widths);
        hasMeasuredWidths.current = true;
      }
    }, 100); // Small delay to ensure rendering is complete

    return () => clearTimeout(timeoutId);
  }, [validData.length, validColumns.length]); // Only remeasure if data or columns change

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  return (
    <div
      className={cn(
        "h-full grid grid-rows-[auto_1fr_auto] overflow-auto",
        className
      )}
    >
      <div>
        {showToolbar ? (
          <div className="flex items-center justify-between gap-2 px-6 py-2 border-b border-border/50">
            <div className="flex items-center gap-2">
              {filterCol ? (
                <div className="w-[240px]">
                  <Input
                    placeholder={filterPlaceholder}
                    value={
                      (() => {
                        const filterValue = filterCol.getFilterValue();
                        return Array.isArray(filterValue)
                          ? filterValue.join(", ")
                          : (filterValue as string) ?? "";
                      })()
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => filterCol.setFilterValue(e.target.value)}
                  />
                </div>
              ) : null}

              {/* Filter slot for custom filters */}
              {filters ? <div className="w-[240px]">{filters}</div> : null}

              {/* Toolbar actions slot (e.g., bulk actions) */}
              {toolbarActions ? (
                <div className="flex items-center gap-2">{toolbarActions}</div>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              {/* Right side actions */}
              {toolbarRightActions ? (
                <div className="flex items-center gap-2">
                  {toolbarRightActions}
                </div>
              ) : null}

              {/* Column visibility using DropdownMenu */}
              {(() => {
                try {
                  const leafColumns = table.getAllLeafColumns();
                  const hideableColumns = (leafColumns || []).filter(
                    (c: Column<TData, unknown>) => c.getCanHide()
                  );

                  if (hideableColumns.length === 0) return null;

                  return (
                    <DropdownMenu
                      trigger={{
                        label: "",
                        icon: <Columns className="h-4 w-4" />,
                        variant: "ghost",
                        size: "sm",
                      }}
                      items={hideableColumns.map(
                        (column: Column<TData, unknown>) => ({
                          type: "checkbox" as const,
                          label:
                            typeof column.columnDef.header === "string"
                              ? (column.columnDef.header as string)
                              : column.id,
                          checked: column.getIsVisible(),
                          onClick: () =>
                            column.toggleVisibility(!column.getIsVisible()),
                        })
                      )}
                      align="end"
                      side="bottom"
                    />
                  );
                } catch (error) {
                  console.warn("Error getting leaf columns:", error);
                  return null;
                }
              })()}
            </div>
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          "overflow-x-auto h-full overflow-auto relative",
          className,
          {
            "": showBorder,
          }
        )}
      >
        <table
          ref={tableRef}
          className="table-text"
          style={{ minWidth: "100%", tableLayout: "auto" }}
        >
          <thead className="table-header-bg sticky top-0 z-0">
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => {
              const hasExpandableRows = getRowCanExpand !== undefined;
              return (
                <tr key={headerGroup.id}>
                  {hasExpandableRows && (
                    <th className="table-header-padding w-10 table-header-bg"></th>
                  )}
                  {headerGroup.headers.map((header: Header<TData, unknown>) => {
                    const canFilter = header.column.getCanFilter();
                    const filterValue = header.column.getFilterValue() as
                      | string[]
                      | undefined;
                    const [searchQuery, setSearchQuery] = React.useState("");

                    // Get unique values for this column from all data
                    const accessorKey =
                      "accessorKey" in header.column.columnDef
                        ? (header.column.columnDef.accessorKey as string)
                        : undefined;
                    const uniqueValues = React.useMemo(() => {
                      if (!canFilter || !accessorKey) return [];
                      const values = new Set<string>();
                      validData.forEach((row) => {
                        const value = (row as any)[accessorKey];
                        if (
                          value !== null &&
                          value !== undefined &&
                          value !== ""
                        ) {
                          const stringValue = String(value);
                          values.add(stringValue);
                        }
                      });
                      return Array.from(values).sort();
                    }, [canFilter, accessorKey, validData]);

                    // Filter options based on search query
                    const filteredOptions = React.useMemo(() => {
                      if (!searchQuery) return uniqueValues;
                      const query = searchQuery.toLowerCase();
                      return uniqueValues.filter((val) =>
                        val.toLowerCase().includes(query)
                      );
                    }, [uniqueValues, searchQuery]);

                    const selectedValues = filterValue || [];

                    const handleCheckboxChange = (
                      value: string,
                      checked: boolean
                    ) => {
                      const currentValues = selectedValues;
                      if (checked) {
                        header.column.setFilterValue([...currentValues, value]);
                      } else {
                        header.column.setFilterValue(
                          currentValues.filter((v) => v !== value)
                        );
                      }
                    };

                    const handleSelectAll = () => {
                      if (selectedValues.length === filteredOptions.length) {
                        // Deselect all filtered options
                        const remaining = selectedValues.filter(
                          (v) => !filteredOptions.includes(v)
                        );
                        header.column.setFilterValue(
                          remaining.length > 0 ? remaining : undefined
                        );
                      } else {
                        // Select all filtered options
                        const newValues = [
                          ...new Set([...selectedValues, ...filteredOptions]),
                        ];
                        header.column.setFilterValue(newValues);
                      }
                    };

                    const allFilteredSelected =
                      filteredOptions.length > 0 &&
                      filteredOptions.every((val) =>
                        selectedValues.includes(val)
                      );
                    const someFilteredSelected = filteredOptions.some((val) =>
                      selectedValues.includes(val)
                    );

                    // Use locked width if available, otherwise use explicit size or minSize
                    const columnSize = header.column.columnDef.size;
                    const columnMinSize = header.column.columnDef.minSize;
                    const lockedWidth = lockedColumnWidths[header.column.id];
                    const widthToUse = lockedWidth
                      ? lockedWidth
                      : columnSize !== undefined && columnSize !== 150
                      ? columnSize
                      : columnMinSize !== undefined
                      ? columnMinSize
                      : undefined;

                    // Calculate left offset for pinned columns
                    const isPinned = header.column.getIsPinned();
                    const pinnedOffset = isPinned
                      ? header.column.getStart(isPinned)
                      : undefined;

                    // Check if this is a group header (has colSpan > 1)
                    const isGroupHeader = header.colSpan > 1;

                    return (
                      <th
                        key={header.id}
                        data-column-id={header.column.id}
                        colSpan={header.colSpan}
                        className={cn(
                          "table-header-padding table-header-text text-left font-semibold",
                          isPinned && "sticky z-10 table-header-bg",
                          isGroupHeader && "table-group-header"
                        )}
                        style={{
                          width: widthToUse ? `${widthToUse}px` : undefined,
                          minWidth: widthToUse ? `${widthToUse}px` : undefined,
                          left: pinnedOffset !== undefined ? `${pinnedOffset}px` : undefined,
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="flex items-center justify-between gap-2">
                            <div
                              className={cn(
                                "flex-1",
                                header.column.getCanSort() &&
                                  "cursor-pointer select-none"
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: " ▲",
                                desc: " ▼",
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                            {canFilter && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                      e.stopPropagation();
                                    }}
                                    className={cn(
                                      "p-1 rounded hover:bg-muted transition-colors",
                                      filterValue &&
                                        filterValue.length > 0 &&
                                        "text-primary"
                                    )}
                                    aria-label="Filter column"
                                  >
                                    <Filter className="h-4 w-4" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-80 p-0"
                                  align="end"
                                  onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                                >
                                  <div className="space-y-3">
                                    <div className="table-text font-semibold border-b border-border/50 px-4 py-3">
                                      <Text
                                        as="div"
                                        className="table-text font-semibold"
                                      >
                                        {` Filter by ${
                                          typeof header.column.columnDef
                                            .header === "string"
                                            ? header.column.columnDef.header
                                            : "values"
                                        }`}
                                      </Text>
                                    </div>

                                    {/* Search Input */}
                                    <div className="px-6">
                                      <Input
                                        type="text"
                                        placeholder="Search values..."
                                        value={searchQuery}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                          setSearchQuery(e.target.value)
                                        }
                                        className="h-8 table-text-small"
                                      />
                                    </div>

                                    <div className="border-t border-border/50">
                                      {/* Select All Checkbox */}
                                      {filteredOptions.length > 0 && (
                                        <div className="flex items-center space-x-2 px-7 py-2 border-b border-border/50">
                                          <Checkbox
                                            checked={allFilteredSelected}
                                            onCheckedChange={handleSelectAll}
                                            ref={(el: HTMLButtonElement | null) => {
                                              if (el && "indeterminate" in el) {
                                                (el as any).indeterminate =
                                                  someFilteredSelected &&
                                                  !allFilteredSelected;
                                              }
                                            }}
                                          />
                                          <label
                                            className="table-text font-semibold cursor-pointer"
                                            onClick={handleSelectAll}
                                          >
                                            Select All ({filteredOptions.length}
                                            )
                                          </label>
                                        </div>
                                      )}

                                      {/* Options List */}
                                      <div className="max-h-60 overflow-y-auto space-y-1 px-4 py-3">
                                        {filteredOptions.length === 0 ? (
                                          <div className="table-text-small text-muted-foreground py-2 text-center">
                                            No values found
                                          </div>
                                        ) : (
                                          filteredOptions.map((value) => (
                                            <div
                                              key={value}
                                              className="flex items-center space-x-2 px-3 py-1.5 hover:bg-accent/30 rounded-md cursor-pointer"
                                              onClick={() =>
                                                handleCheckboxChange(
                                                  value,
                                                  !selectedValues.includes(
                                                    value
                                                  )
                                                )
                                              }
                                            >
                                              <Checkbox
                                                checked={selectedValues.includes(
                                                  value
                                                )}
                                                onCheckedChange={(checked: boolean) =>
                                                  handleCheckboxChange(
                                                    value,
                                                    checked as boolean
                                                  )
                                                }
                                                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                                  e.stopPropagation()
                                                }
                                              />
                                              <label
                                                className="table-text font-medium text-foreground cursor-pointer flex-1"
                                                onClick={(e: React.MouseEvent<HTMLLabelElement>) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                {value}
                                              </label>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                    {/* Selected count */}
                                    {selectedValues.length > 0 && (
                                      <div className="table-text-small text-muted-foreground pt-2 border-t border-border/50 px-4 py-2">
                                        {selectedValues.length} selected
                                      </div>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row: Row<TData>) => {
                const hasExpandableRows = getRowCanExpand !== undefined;
                const canExpand = getRowCanExpand
                  ? getRowCanExpand(row)
                  : false;
                const isExpanded = expandedRows[row.id] || false;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      data-state={row.getIsSelected() && "selected"}
                      className="border-t border-border/50 table-row-hover"
                    >
                      {hasExpandableRows && !canExpand && (
                        <td className="table-cell-padding align-middle w-10"></td>
                      )}
                      {canExpand && (
                        <td className="table-cell-padding align-middle w-10">
                          <button
                            onClick={() => {
                              setExpandedRows(
                                (prev: Record<string, boolean>) => ({
                                  ...prev,
                                  [row.id]: !isExpanded,
                                })
                              );
                            }}
                            className="p-1 hover:bg-muted rounded"
                            aria-label={
                              isExpanded ? "Collapse row" : "Expand row"
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                      )}
                      {row
                        .getVisibleCells()
                        .map((cell: Cell<TData, unknown>) => {
                          // Use same width calculation as header to keep columns aligned
                          const columnSize = cell.column.columnDef.size;
                          const columnMinSize = cell.column.columnDef.minSize;
                          const lockedWidth =
                            lockedColumnWidths[cell.column.id];
                          const widthToUse = lockedWidth
                            ? lockedWidth
                            : columnSize !== undefined && columnSize !== 150
                            ? columnSize
                            : columnMinSize !== undefined
                            ? columnMinSize
                            : undefined;

                          // Calculate left offset for pinned columns
                          const isPinned = cell.column.getIsPinned();
                          const pinnedOffset = isPinned
                            ? cell.column.getStart(isPinned)
                            : undefined;

                          return (
                            <td
                              key={cell.id}
                              className={cn(
                                "table-cell-padding align-middle",
                                isPinned && "sticky z-10 bg-background"
                              )}
                              style={{
                                width: widthToUse
                                  ? `${widthToUse}px`
                                  : undefined,
                                minWidth: columnMinSize
                                  ? `${columnMinSize}px`
                                  : lockedWidth
                                  ? `${lockedWidth}px`
                                  : undefined,
                                left: pinnedOffset !== undefined ? `${pinnedOffset}px` : undefined,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                    </tr>
                    {canExpand && isExpanded && renderExpandedContent && (
                      <tr>
                        <td
                          colSpan={validColumns.length + (canExpand ? 1 : 0)}
                          className="p-0"
                        >
                          {renderExpandedContent(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={validColumns.length}
                  className="px-6 py-10 text-center"
                >
                  {emptyState ?? (
                    <Text as="div" className="text-muted-foreground">
                      No results.
                    </Text>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div>
        {showFooter ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <Text as="div" className="table-text text-muted-foreground">
              {selectedCount} of {table.getFilteredRowModel().rows.length}{" "}
              row(s) selected.
            </Text>

            {(() => {
              const { pageIndex, pageSize } = table.getState().pagination;
              const pageCount = table.getPageCount();

              return (
                <Pagination
                  className="w-full border-t border-border/50"
                  pageNo={pageIndex + 1} // Convert 0-indexed to 1-indexed
                  noOfPage={pageCount}
                  pageSize={pageSize}
                  enablePageSize={enablePageSize}
                  showPageNumber={true}
                  onPageChange={(page: number) => {
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: page - 1, // Convert 1-indexed to 0-indexed
                    }));
                  }}
                  onPageSizeChange={(size: number) => {
                    setPagination({
                      pageIndex: 0, // Reset to first page when page size changes
                      pageSize: size,
                    });
                  }}
                />
              );
            })()}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Convenience select column helpers (native checkbox to avoid extra deps)
export function createSelectColumn<TData>(): ColumnDef<TData> {
  return {
    id: "select",
    enableHiding: false,
    header: ({ table }: { table: Table<TData> }) => {
      const checked = table.getIsAllPageRowsSelected();
      const indeterminate = table.getIsSomePageRowsSelected();
      return (
        <input
          type="checkbox"
          className="size-4"
          checked={checked}
          ref={(el) => {
            if (!el) return;
            el.indeterminate = !checked && indeterminate;
          }}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row }: { row: Row<TData> }) => (
      <input
        type="checkbox"
        className="size-4"
        checked={row.getIsSelected()}
        onChange={(e) => row.toggleSelected(e.target.checked)}
        aria-label="Select row"
      />
    ),
  };
}
